'use client'

import { useAudio } from '@/context/AudioContext'
import { useChapter } from '@/context/ChapterContext'
import { useNavigation } from '@/context/NavigationContext'
import { useUserPrefs } from '@/context/UserPrefsContext'
import { useVideo } from '@/context/VideoContext'
import { getAssetUrl } from '@/utils/assetUtils'
import { useEffect, useMemo, useRef } from 'react'

export default function AudioElement() {
  const {
    isPlaying: audioIsPlaying,
    isMuted,
    volume,
    hasInteracted,
    audioIsReady,
    setAudioReady,
    setAudioPosition,
    setHasInteracted,
    setCurrentAudio,
  } = useAudio()
  const { position: videoPosition, isPlaying: videoIsPlaying } = useVideo()
  const { showChapterMenu, showHotspots, getCurrentChapter } = useChapter()
  const { userPrefs } = useUserPrefs()
  const { view, landingScreen } = useNavigation()

  // Single audio element for all channels
  const audioRef = useRef<HTMLAudioElement>(null)

  // Track last logged state to avoid duplicate logs
  const lastLoggedStateRef = useRef<string | null>(null)

  // Store volatile values in refs to avoid effect re-runs
  const videoIsPlayingRef = useRef(videoIsPlaying)
  videoIsPlayingRef.current = videoIsPlaying

  const hasInteractedRef = useRef(hasInteracted)
  hasInteractedRef.current = hasInteracted

  const videoPositionRef = useRef(videoPosition)
  videoPositionRef.current = videoPosition

  const sources = useMemo(
    () => ({
      hotspots: getAssetUrl('/audio/hotspots.mp3'),
      menu: getAssetUrl('/audio/hotspots.mp3'), // Reuse hotspots track for menu
      intro: getAssetUrl('/audio/intro.mp3'),
      outro: getAssetUrl('/audio/outro.mp3'),
      silence: getAssetUrl('/audio/silence.mp3'),
    }),
    []
  )

  // Store sources in ref for event handlers
  const sourcesRef = useRef(sources)
  sourcesRef.current = sources

  // Get the current chapter object from context
  const chapter = getCurrentChapter()

  // Determine which audio channel should be active
  type Channel = 'menu' | 'hotspots' | 'chapter' | 'intro' | 'outro' | 'silence'

  const desired: Channel = useMemo(() => {
    if (view === 'landing') {
      return landingScreen === 'splash' ? 'intro' : 'menu'
    }
    if (view === 'credits') {
      return 'outro'
    }
    if (showChapterMenu) {
      return 'menu'
    }
    if (showHotspots) {
      return 'hotspots'
    }
    return chapter ? 'chapter' : 'silence'
  }, [view, landingScreen, showChapterMenu, showHotspots, chapter])

  // Store desired in ref for event handlers
  const desiredRef = useRef(desired)
  desiredRef.current = desired

  // Get the source URL for the desired channel
  const desiredSrc = useMemo(() => {
    if (desired === 'chapter' && chapter) {
      if (userPrefs.language === 'english' && chapter.englishAudio) {
        return chapter.englishAudio
      }
      if (chapter.maoriAudio) {
        return chapter.maoriAudio
      }
      return sources.silence
    }

    return sources[desired as keyof typeof sources] || sources.silence
  }, [desired, chapter, userPrefs.language, sources])

  // Determine loop behavior based on channel
  const shouldLoop = useMemo(() => {
    return desired !== 'chapter' // All non-chapter audio loops
  }, [desired])

  // Update audio properties (mute/volume/loop)
  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    el.muted = isMuted
    el.volume = volume
    el.loop = shouldLoop
  }, [isMuted, volume, shouldLoop])

  // Swap audio source when desired channel changes
  useEffect(() => {
    const el = audioRef.current
    if (!el || !desiredSrc) return

    // el.src returns absolute URL, desiredSrc may be relative
    // Normalize by resolving desiredSrc to absolute URL for comparison
    const absoluteDesiredSrc = new URL(desiredSrc, window.location.origin).href

    if (el.src !== absoluteDesiredSrc) {
      console.log({ changingTo: desired, newSrc: desiredSrc, from: el.src }, 'audio')

      // Reset audio ready state when changing source
      setAudioReady(false)

      // For smoother transitions, don't pause explicitly
      // Set new source and load in next frame to avoid blocking
      el.src = desiredSrc

      requestAnimationFrame(() => {
        el.load()

        // After loading new source, attempt to play if needed
        const overlay = desired !== 'chapter'
        const shouldPlay = (overlay || videoIsPlayingRef.current) && hasInteractedRef.current

        if (shouldPlay) {
          // Wait for the new source to be ready before playing
          const tryPlay = () => {
            el.play().catch((e) => {
              if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') {
                console.error('Error playing audio after source change:', e)
              }
            })
          }

          if (el.readyState >= 2) {
            // HAVE_CURRENT_DATA or better
            tryPlay()
          } else {
            el.addEventListener('loadeddata', tryPlay, { once: true })
          }
        }
      })
    }
  }, [desiredSrc, desired, setAudioReady])

  // Sync chapter audio position to video position - only on significant drift
  // Use an interval instead of reacting to every videoPosition change
  useEffect(() => {
    if (desired !== 'chapter') return

    const el = audioRef.current
    if (!el) return

    const syncInterval = setInterval(() => {
      // Don't sync until audio is ready to avoid stuttering during load
      if (el.readyState < 2) return

      const timeDiff = Math.abs(el.currentTime - videoPositionRef.current)
      // Only seek if difference is significant (more than 2 seconds) to avoid stuttering
      if (timeDiff > 2.0) {
        console.log(
          ['Audio sync: correcting drift', { audioCurrent: el.currentTime, videoPosition: videoPositionRef.current }],
          'audio'
        )
        el.currentTime = videoPositionRef.current
      }
    }, 1000) // Check once per second instead of on every position update

    return () => clearInterval(syncInterval)
  }, [desired])

  // Handle play/pause logic
  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    const overlay = desired !== 'chapter'
    const shouldPlay = (overlay || videoIsPlaying) && hasInteracted

    // Log state changes for debugging
    const stateKey = `${desired}-${videoIsPlaying}-${hasInteracted}-${audioIsReady}-${shouldPlay}`
    if (lastLoggedStateRef.current !== stateKey) {
      lastLoggedStateRef.current = stateKey
      console.log(
        [
          'Audio play/pause decision',
          {
            desired,
            overlay,
            videoIsPlaying,
            hasInteracted,
            audioIsReady,
            shouldPlay,
            paused: el.paused,
            readyState: el.readyState,
            src: el.src?.split('/').pop(),
          },
        ],
        'audio'
      )
    }

    if (shouldPlay) {
      // Always attempt to play if should play, even if not ready yet
      // The browser will queue the play and execute when ready
      if (el.paused) {
        el.play().catch((e) => {
          if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') {
            console.error('Error playing audio:', e)
          }
        })
      }
    } else {
      if (!el.paused) {
        el.pause()
      }
    }
  }, [desired, videoIsPlaying, hasInteracted, audioIsReady])

  // Track audio position and readiness - setup event listeners ONCE
  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    const handleTimeUpdate = () => setAudioPosition(el.currentTime)

    const handleCanPlay = () => {
      setAudioReady(true)

      // When audio becomes ready, check if we should be playing
      const overlay = desiredRef.current !== 'chapter'
      const shouldPlay = (overlay || videoIsPlayingRef.current) && hasInteractedRef.current

      if (shouldPlay && el.paused) {
        console.log(
          [
            'Audio became ready, auto-playing',
            {
              desired: desiredRef.current,
              videoIsPlaying: videoIsPlayingRef.current,
              hasInteracted: hasInteractedRef.current,
            },
          ],
          'audio'
        )
        el.play().catch((e) => {
          if (e.name !== 'AbortError' && e.name !== 'NotAllowedError') {
            console.error('Error auto-playing audio when ready:', e)
          }
        })
      }
    }

    const handleEnded = () => {
      // Only handle ended for chapter audio - let it naturally end
      // Don't force silence, let the desired channel logic handle it
      if (desiredRef.current === 'chapter') {
        console.log(['Chapter audio ended'], 'audio')
        // The chapter will advance or the user will interact - don't force silence
      }
    }

    el.addEventListener('timeupdate', handleTimeUpdate)
    el.addEventListener('canplay', handleCanPlay)
    el.addEventListener('ended', handleEnded)

    // Check if audio is already ready (e.g., from browser cache)
    if (el.readyState >= 3) {
      // HAVE_FUTURE_DATA or better
      setAudioReady(true)
    }

    return () => {
      el.removeEventListener('timeupdate', handleTimeUpdate)
      el.removeEventListener('canplay', handleCanPlay)
      el.removeEventListener('ended', handleEnded)
    }
  }, [setAudioPosition, setAudioReady]) // Only depend on stable setters

  // Keep AudioContext state aligned for consumers like SubtitleDisplay
  useEffect(() => {
    if (desired === 'chapter' && desiredSrc) {
      const t = audioRef.current?.currentTime ?? 0
      setCurrentAudio(desiredSrc, t, 'chapter', false)
    } else {
      // Map non-chapter channels to AudioType for context
      let type: 'intro' | 'outro' | 'silence' | 'hotspots'
      if (desired === 'intro' || desired === 'outro') {
        type = desired
      } else if (desired === 'silence') {
        type = 'silence'
      } else {
        type = 'hotspots'
      }
      setCurrentAudio(null, 0, type, false)
    }
  }, [desired, desiredSrc, setCurrentAudio])

  // Initial setup: ensure we start with no auto-play until user interacts
  useEffect(() => {
    setHasInteracted(false)
    const onFirstInteract = () => {
      setHasInteracted(true)
    }
    window.addEventListener('pointerdown', onFirstInteract, { once: true })
    window.addEventListener('keydown', onFirstInteract, { once: true })
    window.addEventListener('click', onFirstInteract, { once: true })
    return () => {
      window.removeEventListener('pointerdown', onFirstInteract)
      window.removeEventListener('keydown', onFirstInteract)
      window.removeEventListener('click', onFirstInteract)
    }
  }, [setHasInteracted])

  return (
    <audio
      ref={audioRef}
      style={{ display: 'none' }}
      preload="auto"
      crossOrigin="anonymous"
      playsInline
      // Performance optimizations for mobile
      webkit-playsinline="true"
      x-webkit-airplay="deny"
      data-testid="audio-element"
    >
      <track kind="captions" />
    </audio>
  )
}
